import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Select } from '@opendate-app/setlist';

export const useUpgradeQuote = (changesTicketType, destination, onPreview) => {
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    setQuote(null);
    if (!changesTicketType || !onPreview) return undefined;
    let current = true;
    onPreview(destination)
      .then((data) => {
        if (!current) return;
        const refundable = data.refundableDifference === null || data.refundableDifference === undefined ? null : Number(data.refundableDifference);
        setQuote({ priceDifference: Number(data.priceDifference), total: Number(data.total), refundableDifference: refundable });
      })
      .catch((error) => {
        const errors = error && error.response && error.response.data && error.response.data.errors;
        if (current) setQuote({ error: errors ? [].concat(errors).join(', ') : "Couldn't work out the price difference. Close this and try again." });
      });
    return () => { current = false; };
  }, [changesTicketType, destination]); // eslint-disable-line react-hooks/exhaustive-deps

  return quote;
};

export const upgradeReady = (changesTicketType, quote, paymentMethod) => (
  !changesTicketType || Boolean(quote && !quote.error && (quote.priceDifference <= 0 || paymentMethod))
);

export const upgradePaymentMethod = (quote, paymentMethod) => (quote && !quote.error && quote.priceDifference > 0 ? paymentMethod : null);

export const upgradeRefundAmount = (quote, refundDifference) => (
  refundDifference && quote && !quote.error && quote.refundableDifference ? quote.refundableDifference : null
);

export const upgradeExpectedTotal = (quote, paymentMethod) => (upgradePaymentMethod(quote, paymentMethod) ? quote.total : null);

const UpgradePaymentFields = ({
  changesTicketType, quote, paidAmount, currency, paymentMethods, paymentMethod, onChange, canRefund, refundDifference, onRefundDifferenceChange,
}) => {
  const money = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  const keeps = <p className="op-muted">The fan keeps the {money(paidAmount)} they paid and the same barcode.</p>;

  if (!changesTicketType) return keeps;
  if (quote === null) return <p className="op-muted">Working out the price difference…</p>;
  if (quote.error) return <p className="op-muted">{quote.error}</p>;
  if (quote.refundableDifference && canRefund) {
    return (
      <>
        {keeps}
        <Checkbox
          label={`Refund the ${money(quote.refundableDifference)} difference`}
          checked={refundDifference}
          onChange={(event) => onRefundDifferenceChange(event.target.checked)}
        />
      </>
    );
  }
  if (quote.priceDifference <= 0) return keeps;

  return (
    <>
      <p style={{ marginTop: 12, marginBottom: 0, fontWeight: 600 }}>Collect {money(quote.total)}</p>
      <p className="op-muted">
        {money(quote.priceDifference)} difference
        {Number(quote.total) > Number(quote.priceDifference) && ` plus ${money(quote.total - quote.priceDifference)} door fees and taxes`}
      </p>
      <Select
        label="How did the fan pay?"
        placeholder="Pick a payment method"
        options={paymentMethods.map((method) => ({ value: method, label: method }))}
        value={paymentMethod}
        onChange={(value) => onChange(value)}
      />
    </>
  );
};

UpgradePaymentFields.propTypes = {
  changesTicketType: PropTypes.bool.isRequired,
  quote: PropTypes.shape({
    priceDifference: PropTypes.number,
    total: PropTypes.number,
    refundableDifference: PropTypes.number,
    error: PropTypes.string,
  }),
  paidAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  paymentMethod: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  canRefund: PropTypes.bool,
  refundDifference: PropTypes.bool,
  onRefundDifferenceChange: PropTypes.func,
};

UpgradePaymentFields.defaultProps = {
  quote: null,
  paidAmount: 0,
  currency: null,
  paymentMethods: [],
  paymentMethod: null,
  canRefund: false,
  refundDifference: false,
  onRefundDifferenceChange: () => {},
};

export default UpgradePaymentFields;
